
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { axios } from '@/helpers/axios';
import { AxiosResponse } from 'axios';
import ChartHeading from '@/components/DataTab/Charts/ChartComponents/ChartHeading.vue';
import ChartComponent from '@/components/DataTab/Charts/ChartComponents/ChartComponent.vue';
import {
  ChartLoadingStatus,
  DiversionDemandDataType,
  LayoutType,
  PlotlyTraceType,
  ScenarioNameType,
} from '@/types';
import { colors } from '@/assets/colors/colors';

type DemandCategory = 'iwr' | 'demand_avg' | 'demand_dry' | 'demand_wet';
@Component({
  components: { ChartComponent, ChartHeading },
})
export default class DiversionDemandChart extends Vue {
  @Prop() readonly areaId!: number;
  @Prop() readonly chartId!: number;
  @Prop({ type: Boolean, default: false }) readonly showTooltip!: boolean;

  chartData: DiversionDemandDataType[] = [];
  dataStatus: ChartLoadingStatus = 'idle';

  selectedScenario: ScenarioNameType = 'Baseline';
  demandCategories: DemandCategory[] = [
    'iwr',
    'demand_avg',
    'demand_dry',
    'demand_wet',
  ];

  diversionColorMap: Record<DemandCategory, string> = {
    iwr: colors.darkestBlue,
    demand_wet: colors.mediumBlue,
    demand_avg: colors.teal,
    demand_dry: colors.purple,
  };

  mounted() {
    // fetch data
    this.dataStatus = 'loading';
    axios
      .get(`/charts/${this.chartId}/${this.areaId}`)
      .then((response: AxiosResponse) => {
        this.chartData = response.data.data;
        this.dataStatus = 'success';
      })
      .catch(() => (this.dataStatus = 'error'));

    // set default selected scenario
    this.setDefaultScenario();
  }

  setDefaultScenario() {
    // select baseline if exists, else pick first scenario
    if (this.scenarioList.includes('Baseline')) {
      this.selectedScenario = 'Baseline';
    } else {
      this.selectedScenario = this.scenarioList[0];
    }
  }

  get scenarioList(): ScenarioNameType[] {
    return this.$store.getters.selectedScenarioList;
  }

  get plotlyData() {
    const traces: PlotlyTraceType[] = [];

    const filteredBySelectedScenario = this.chartData.filter(
      item => item.scenario === this.selectedScenario,
    );

    this.demandCategories.forEach(cat => {
      const catName = this.$t(
        `data.chartTables.${this.chartId}.categories.${cat}`,
      );
      const demandTrace: PlotlyTraceType = {
        x: [],
        y: [],
        name: catName,
        type: 'bar',
        marker: { color: this.diversionColorMap[cat] },
        hovertemplate: `<b>${catName}:</b> %{y:,.2f}<extra></extra>`,
      };
      filteredBySelectedScenario.forEach(item => {
        demandTrace.x.push(item.basin);
        demandTrace.y.push(item[cat]);
      });
      traces.push(demandTrace);
    });

    return traces;
  }

  get layout(): LayoutType {
    const label = this.$t(`data.chartTables.${this.chartId}.axisLabels.y`);
    const unit = this.$t(`data.chartTables.${this.chartId}.unit`);

    return {
      barmode: 'group',
      yaxis: {
        fixedrange: true,
        automargin: true,
        title: {
          text: `${label}(${unit})`,
          standoff: 10,
        },
        hoverformat: '.2f',
      },
      xaxis: {
        fixedrange: true,
      },
      hovermode: 'closest',
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      legend: {
        x: 0,
        y: -0.35,
        orientation: 'h',
      },
    };
  }

  @Watch('scenarioList')
  scenarioListChanged() {
    this.setDefaultScenario();
  }
}
